import { useCallback, useEffect, useRef, useState } from 'react';
import { BordOneToneIcon, NudosButton } from 'components/NudosComponents';
import { InudosButton } from 'components/NudosComponents/NudosButton/NudosButton';
import BordAnimationLoader from 'components/BordAnimationLoader/BordAnimationLoader';
import { XSign } from 'assets/images/components/NudosComponents';
import { IQrCard } from '../QRcard/QRcard';
import { getQRCode } from 'services/orders';
import { displayErrorNotification } from 'utils/displayNudosStandardNotifications';
import { generateIndividualQrPDF } from 'utils/pdfGeneration';
import HiddenQRList from '../HiddenQRList/HiddenQRList';

import './BordDownloadQrModalContent.scss';

interface IBordDownloadQrModalContent {
  buttonProps: InudosButton;
  qrDataProps: IQrCard[];
  closeModalCallback: () => void;
  onGenerationComplete?: () => void;
}

const BordDownloadQrModalContent = ({
  closeModalCallback,
  onGenerationComplete,
  qrDataProps
}: IBordDownloadQrModalContent) => {
  const hiddenQRListRef = useRef<HTMLDivElement>(null);
  const [qrDataWithCodes, setQrDataWithCodes] = useState<IQrCard[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [generatedPDFs, setGeneratedPDFs] = useState<Blob[]>([]);
  const [error, setError] = useState<string | null>(null);

  const downloadSuccess = progress === 100;
  const downloadPercentClass = downloadSuccess ? 'downloadSuccess' : 'downloading';

  const downloadAllPDFs = useCallback(async () => {
    const batchSize = 50;
    const batches = Math.ceil(generatedPDFs.length / batchSize);

    for (let batch = 0; batch < batches; batch++) {
      const start = batch * batchSize;
      const end = Math.min(start + batchSize, generatedPDFs.length);
      const currentBatch = generatedPDFs.slice(start, end);

      for (let i = 0; i < currentBatch.length; i++) {
        const pdf = currentBatch[i];
        const globalIndex = start + i;
        const fileName = `${qrDataProps[globalIndex].qrData.productId}-${qrDataProps[globalIndex].qrData.serial}-.pdf`;

        const url = window.URL.createObjectURL(pdf);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      if (batch < batches - 1) {
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    }
  }, [generatedPDFs, qrDataProps]);

  const generateQrCodesForDataList = async (
    qrDataList: IQrCard[],
    setQrDataWithCodes: (data: IQrCard[]) => void,
    onError: () => void
  ) => {
    try {
      const qrsWithCodes = await Promise.all(
        qrDataList?.map(async qr => {
          try {
            const qrCode = await getQRCode(Number(qr?.qrData?.productId));
            return {
              ...qr,
              qrCode
            };
          } catch (error) {
            return qr;
          }
        })
      );
      setQrDataWithCodes(qrsWithCodes);
    } catch (error) {
      onError();
    }
  };

  useEffect(() => {
    if (qrDataProps.length > 0) {
      generateQrCodesForDataList(qrDataProps, setQrDataWithCodes, displayErrorNotification);
    }
  }, [qrDataProps]);

  const handleMassiveQrPDFGeneration = (
    qrDataWithCodes: IQrCard[],
    hiddenQRListRef: React.RefObject<HTMLDivElement>,
    setIsGenerating: (value: boolean) => void,
    setError: (error: string | null) => void,
    setGeneratedPDFs: (pdfs: Blob[]) => void,
    setProgress: (progress: number) => void,
    onGenerationComplete?: () => void
  ) => {
    let mounted = true;
    let generationStarted = false;

    const generateAllPDFs = async () => {
      if (!mounted) return;
      if (generationStarted) return;
      if (qrDataWithCodes?.length === 0) return;
      if (!hiddenQRListRef.current) return;

      generationStarted = true;
      setIsGenerating(true);
      setError(null);
      setGeneratedPDFs([]);

      try {
        const qrElements = hiddenQRListRef.current.querySelectorAll('.qrItem');
        const totalQRs = qrElements.length;
        const pdfs: Blob[] = [];

        for (let i = 0; i < totalQRs; i++) {
          if (!mounted) break;

          const qrElement = qrElements[i];
          const pdfBlob = await generateIndividualQrPDF(qrElement);
          pdfs.push(pdfBlob);

          const currentProgress = Math.floor(((i + 1) / totalQRs) * 100);
          setProgress(currentProgress);
        }

        setGeneratedPDFs(pdfs);
        if (pdfs.length > 0) {
          onGenerationComplete?.();
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Error desconocido al generar PDFs';
        setError(errorMessage);
      } finally {
        setIsGenerating(false);
      }
    };

    if (qrDataWithCodes?.length > 0) {
      generateAllPDFs();
    }

    return () => {
      mounted = false;
    };
  };

  const handleDownloadAllPDFs = async () => {
    try {
      await downloadAllPDFs();
      closeModalCallback();
    } catch (error) {
      displayErrorNotification();
    }
  };

  useEffect(() => {
    const cleanup = handleMassiveQrPDFGeneration(
      qrDataWithCodes,
      hiddenQRListRef,
      setIsGenerating,
      setError,
      setGeneratedPDFs,
      setProgress,
      onGenerationComplete
    );

    return cleanup;
  }, [qrDataWithCodes]);

  return (
    <div className="bordDownloadQrModalContent">
      <div className="bordDownloadQrModalContentHeader" onClick={closeModalCallback}>
        <XSign fill="#B3B3B3" />
      </div>
      <div className="mt-8">
        {downloadSuccess ? (
          <BordOneToneIcon variant="checkCircle" stroke="#81BC3F" />
        ) : (
          <BordAnimationLoader lottieCustomClassName="h-10" />
        )}
      </div>

      <div className="textStylesContainer">
        <span className="text-nudos-text-color">Procesando archivo PDF</span>
        <span className={`downloadPercent ${downloadPercentClass}`}>({progress}% completado).</span>
      </div>
      <NudosButton
        buttonText="Descargar"
        customWidth="180px"
        customHeight="38px"
        isButtonDisabled={!downloadSuccess || isGenerating || error !== null || generatedPDFs.length === 0}
        handleClick={handleDownloadAllPDFs}
      />
      {qrDataWithCodes?.length > 0 && <HiddenQRList ref={hiddenQRListRef} qrList={qrDataWithCodes} />}
    </div>
  );
};

export default BordDownloadQrModalContent;
